import {
  InterviewAccessPurpose,
  InterviewRecoveryReason,
  type InterviewEntryPage,
  type InterviewEntryReads,
  type InterviewEntryRequest,
  type InterviewSessionBootstrap,
  type InterviewSessionResponse,
} from 'api/generated/types';
import { isAxiosError } from 'axios';
import { safeJsonParse } from 'utils/json';
import { sessionStore } from 'utils/typedStorage';
import {
  isEnumValue,
  isFiniteNumber,
  isNonEmptyString,
  isRecord,
} from 'utils/typeGuards';

import type { CandidateContext } from './candidateContext';
import {
  bindCandidateRequestTransport,
  canonicalCandidateRequestUrl,
  mapCandidateLaunchUrl,
} from './requestTransport';

export const CANDIDATE_CAPABILITY_HEADER = 'x-candidate-capability';
export const CANDIDATE_CAPABILITY_INVALID_DETAIL =
  'candidate_capability_invalid';
const CANDIDATE_CAPABILITY_MINT_PATH = '/candidate_capability';
const SESSION_PREFIX = 'interviewSession:';
const LAUNCH_NONCE_PREFIX = 'interviewLaunchNonce:';
const EXCHANGE_TIMEOUT_MS = 15_000;

export class CandidateSessionStorageError extends Error {
  constructor() {
    super('Interview session storage is unavailable in this tab');
    this.name = 'CandidateSessionStorageError';
  }
}

export type { CandidateCredential, CandidateContext } from './candidateContext';
export type CandidateRouteParams = {
  encodedId?: string;
  interviewId?: string;
  encodedTestData?: string;
  encodedPracticeData?: string;
  interviewSession?: string;
  launch?: string;
};

type StoredSession = {
  session: InterviewSessionResponse;
  /** Retained only after the server has successfully exchanged this invitation. */
  invitation?: string;
  transportId?: string;
};
const sessions = new Map<string, StoredSession>();
const inFlight = new Map<string, Promise<InterviewSessionResponse>>();
const launchNonces = new Map<string, string>();
const resolvedLaunches = new Map<string, string>();
let currentRouteParams: CandidateRouteParams | null = null;
const STATIC_INTERVIEW_SEGMENTS = new Set([
  'error',
  'video-help',
  'screen-share-help',
  'test-call',
  'practice',
  'session',
]);

function launchContext(launch: string): CandidateContext {
  let nonce =
    sessionStore.getDynamic(`${LAUNCH_NONCE_PREFIX}${launch}`) ??
    launchNonces.get(launch) ??
    undefined;
  if (!nonce) {
    nonce = crypto.randomUUID();
  }
  launchNonces.set(launch, nonce);
  return { kind: 'launch', launch_credential: launch, redemption_nonce: nonce };
}

export function deriveCandidateCredential(
  pathname: string | undefined,
  params: CandidateRouteParams | null
): CandidateContext | null {
  if (params?.launch) return launchContext(params.launch);
  if (params?.interviewSession)
    return { kind: 'session', session_id: params.interviewSession };
  const path = pathname ?? '';
  const match = path.match(/^\/(?:web-interview|web-call)\/([^/?#]+)/);
  if (match?.[1] && !STATIC_INTERVIEW_SEGMENTS.has(match[1])) {
    return { kind: 'web_call_link', encoded_id: match[1] };
  }
  // Browser-encoded test/practice data and resource ids are never credentials.
  return params?.encodedId
    ? { kind: 'web_call_link', encoded_id: params.encodedId }
    : null;
}

export function setCandidateRouteParams(params: CandidateRouteParams): void {
  currentRouteParams = params;
}
export function clearCandidateRouteParams(params: CandidateRouteParams): void {
  if (currentRouteParams === params) currentRouteParams = null;
}
export function getCurrentCandidateCredential(): CandidateContext | null {
  if (currentRouteParams)
    return bindCandidateRequestTransport(
      deriveCandidateCredential(undefined, currentRouteParams)
    );
  if (typeof window === 'undefined') return null;
  const query = new URLSearchParams(window.location.search);
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  return bindCandidateRequestTransport(
    deriveCandidateCredential(window.location.pathname, {
      interviewSession: query.get('interviewSession') ?? undefined,
      encodedId: query.get('encodedId') ?? undefined,
      launch: fragment.get('launch') ?? undefined,
    })
  );
}
export function captureCandidateContext(): CandidateContext | null {
  const context = getCurrentCandidateCredential();
  if (!context) return null;
  const session = findSession(context);
  return session
    ? {
        kind: 'session',
        session_id: session.session.session_id,
        ...(session.transportId ? { transportId: session.transportId } : {}),
      }
    : context;
}

export function isCapabilityMintUrl(url: string | undefined): boolean {
  if (!url) return false;
  const path = new URL(
    canonicalCandidateRequestUrl(url),
    'https://interview.invalid'
  ).pathname;
  return [
    CANDIDATE_CAPABILITY_MINT_PATH,
    '/interview_sessions/launch/exchange',
    '/interview_sessions/refresh',
  ].includes(path);
}

function tokenClaims(token: string): Record<string, unknown> | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const claims = safeJsonParse(
      atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))
    );
    return isRecord(claims) ? claims : null;
    // swallow-ok: parsing claims is only a local expiry hint, never authorization.
  } catch {
    return null;
  }
}
export function capabilityTokenExpiryMs(token: string): number | null {
  const claims = tokenClaims(token);
  return claims && isFiniteNumber(claims.exp) ? claims.exp * 1000 : null;
}

function readPersisted(key: string): string | null {
  try {
    return sessionStore.getPersisted(key);
  } catch {
    throw new CandidateSessionStorageError();
  }
}

function requirePersistence(key: string, value: string): void {
  if (readPersisted(key) === value) return;
  if (!sessionStore.setPersisted(key, value) || readPersisted(key) !== value) {
    throw new CandidateSessionStorageError();
  }
}

function persist(stored: StoredSession, requireDurable = false): void {
  const key = `${SESSION_PREFIX}${stored.session.session_id}`;
  const value = JSON.stringify(stored);
  if (requireDurable) requirePersistence(key, value);
  else sessionStore.setDynamic(key, value);
  // Server-issued grants remain valid in this page even if their reload mirror
  // fails. A one-time launch must persist before its URL authority is replaced.
  sessions.set(stored.session.session_id, stored);
}

/** Metadata changes retain the already persisted refresh authority. */
function updateSession(stored: StoredSession): void {
  // Revocation and recovery status are revalidated by the server. The page still
  // needs alias removal/token invalidation when browser storage is unavailable.
  persist(stored);
}
function isSessionBootstrap(
  value: unknown
): value is InterviewSessionBootstrap {
  if (!isRecord(value) || !isNonEmptyString(value.job_id)) return false;
  const optionalStrings = [
    'candidate_id',
    'call_id',
    'candidate_campaign_id',
    'draft_id',
    'candidate_name',
    'voice_name',
    'selected_language',
    'call_software',
    'livekit_url',
    'candidate_timezone',
    'tts_speaker_tag',
  ] as const;
  const ttsV4Settings = value.tts_v4_settings;
  const ttsV4SettingsValid =
    ttsV4Settings == null ||
    (isRecord(ttsV4Settings) &&
      (ttsV4Settings.stability == null ||
        typeof ttsV4Settings.stability === 'number') &&
      (ttsV4Settings.similarity == null ||
        typeof ttsV4Settings.similarity === 'number') &&
      (ttsV4Settings.clone_strength == null ||
        typeof ttsV4Settings.clone_strength === 'number'));
  return (
    ttsV4SettingsValid &&
    optionalStrings.every(
      (key) => value[key] == null || typeof value[key] === 'string'
    ) &&
    (value.recovery_reason == null ||
      isEnumValue(InterviewRecoveryReason, value.recovery_reason)) &&
    (value.recovery_interview_completed === undefined ||
      typeof value.recovery_interview_completed === 'boolean') &&
    (value.recovery_request_pending === undefined ||
      typeof value.recovery_request_pending === 'boolean') &&
    (value.intake_completed === undefined ||
      typeof value.intake_completed === 'boolean') &&
    (value.contact_verification_required === undefined ||
      typeof value.contact_verification_required === 'boolean') &&
    (value.use_local_livekit_agent === undefined ||
      typeof value.use_local_livekit_agent === 'boolean')
  );
}
function readSession(
  sessionId: string,
  requireReadable = false
): StoredSession | undefined {
  const cached = sessions.get(sessionId);
  if (cached) return cached;
  const stored = parseStoredSession(
    requireReadable
      ? readPersisted(`${SESSION_PREFIX}${sessionId}`)
      : sessionStore.getDynamic(`${SESSION_PREFIX}${sessionId}`),
    sessionId
  );
  if (stored) sessions.set(sessionId, stored);
  return stored;
}
function parseStoredSession(
  raw: string | null,
  sessionId: string
): StoredSession | undefined {
  if (!raw) return undefined;
  const parsed: unknown = safeJsonParse(raw);
  if (!isRecord(parsed) || !isRecord(parsed.session)) return undefined;
  // Persisted storage is untrusted. The server verifies the token and refresh secret;
  // validate the structure before passing it back to the generated client.
  const value = parsed.session;
  if (
    !isNonEmptyString(value.session_id) ||
    value.session_id !== sessionId ||
    !isNonEmptyString(value.capability_token) ||
    !isNonEmptyString(value.refresh_token) ||
    !isNonEmptyString(value.expires_at) ||
    !Number.isFinite(Date.parse(value.expires_at)) ||
    !isEnumValue(InterviewAccessPurpose, value.purpose) ||
    !isSessionBootstrap(value.bootstrap) ||
    (parsed.invitation !== undefined && !isNonEmptyString(parsed.invitation)) ||
    (parsed.transportId !== undefined && !isNonEmptyString(parsed.transportId))
  )
    return undefined;
  const stored: StoredSession = {
    session: {
      session_id: value.session_id,
      capability_token: value.capability_token,
      refresh_token: value.refresh_token,
      expires_at: value.expires_at,
      purpose: value.purpose,
      bootstrap: value.bootstrap,
    },
    invitation: parsed.invitation,
    transportId: parsed.transportId,
  };
  return stored;
}

function requirePersistedAuthority(stored: StoredSession): void {
  const durable = parseStoredSession(
    readPersisted(`${SESSION_PREFIX}${stored.session.session_id}`),
    stored.session.session_id
  );
  const credentialFields = [
    'session_id',
    'capability_token',
    'refresh_token',
    'expires_at',
    'purpose',
  ] as const;
  const bootstrapResources = [
    'job_id',
    'candidate_id',
    'candidate_campaign_id',
    'call_id',
    'draft_id',
  ] as const;
  if (
    !durable ||
    durable.transportId !== stored.transportId ||
    !credentialFields.every(
      (field) => durable.session[field] === stored.session[field]
    ) ||
    !bootstrapResources.every(
      (field) =>
        durable.session.bootstrap[field] === stored.session.bootstrap[field]
    )
  ) {
    // The current page holds the acknowledged grant. Repair a stale or missing
    // mirror before discarding the launch URL; never adopt its different data.
    requirePersistence(
      `${SESSION_PREFIX}${stored.session.session_id}`,
      JSON.stringify(stored)
    );
  }
  // Pending status, display metadata and invitation aliases do not change the
  // signed session's authority. Their optional mirrors may lag behind this page.
}
function hydrateSessions(): void {
  for (const key of sessionStore.keys()) {
    if (key.startsWith(SESSION_PREFIX))
      readSession(key.slice(SESSION_PREFIX.length));
  }
}

function findSession(context: CandidateContext): StoredSession | undefined {
  if (context.kind === 'session') return readSession(context.session_id);
  if (context.kind === 'launch') {
    const id = resolvedLaunches.get(context.launch_credential);
    return id ? readSession(id) : undefined;
  }
  const current = [...sessions.values()].find(
    (stored) =>
      stored.invitation === context.encoded_id &&
      stored.transportId === context.transportId
  );
  if (current) return current;
  hydrateSessions();
  return [...sessions.values()].find(
    (stored) =>
      stored.invitation === context.encoded_id &&
      stored.transportId === context.transportId
  );
}
/** Explicit invitation-screen retry: require the original link to be revalidated. */
export function forgetInvitationSession(
  context: Extract<CandidateContext, { kind: 'web_call_link' }>
): void {
  // Load persisted aliases too, while keeping every SID's grant isolated.
  hydrateSessions();
  for (const stored of sessions.values()) {
    if (
      stored.invitation === context.encoded_id &&
      stored.transportId === context.transportId
    ) {
      updateSession({
        session: stored.session,
        ...(stored.transportId ? { transportId: stored.transportId } : {}),
      });
    }
  }
}

export function getCandidateSession(
  context: CandidateContext | null = captureCandidateContext()
): InterviewSessionResponse | null {
  return context ? (findSession(context)?.session ?? null) : null;
}
function contextKey(context: CandidateContext): string {
  if (context.transportId)
    return `${context.transportId}:${contextKey({ ...context, transportId: undefined })}`;
  switch (context.kind) {
    case 'session':
      return `session:${context.session_id}`;
    case 'web_call_link':
      return `invitation:${context.encoded_id}`;
    case 'launch':
      return `launch:${context.launch_credential}`;
  }
}

/** Establish access only from a successful server response, bound to the initiating context. */
export function acceptCandidateSession(
  response: InterviewSessionResponse,
  context: CandidateContext
): void {
  if (
    !response.session_id ||
    !response.capability_token ||
    !response.refresh_token ||
    !Number.isFinite(Date.parse(response.expires_at))
  ) {
    throw new Error('Interview session exchange returned no credential');
  }
  if (
    context.kind === 'session' &&
    context.session_id !== response.session_id
  ) {
    throw new Error('Interview session response does not match its request');
  }
  persist(
    {
      session: response,
      ...(context.transportId
        ? { transportId: context.transportId }
        : findSession(context)?.transportId
          ? { transportId: findSession(context)?.transportId }
          : {}),
      invitation:
        context.kind === 'web_call_link'
          ? context.encoded_id
          : findSession(context)?.invitation,
    },
    context.kind === 'launch'
  );
  if (context.kind === 'launch') {
    resolvedLaunches.set(context.launch_credential, response.session_id);
  }
}

/** Mirror a successful server-side recovery request in this tab's bootstrap. */
export function markRecoveryRequestPending(sessionId: string): void {
  const stored = readSession(sessionId);
  if (stored?.session.purpose !== InterviewAccessPurpose.recovery) return;
  updateSession({
    ...stored,
    session: {
      ...stored.session,
      bootstrap: {
        ...stored.session.bootstrap,
        recovery_request_pending: true,
      },
    },
  });
}

/** Store an explicitly authorized transition without changing the active route or its source. */
export function adoptCandidateSession(
  response: InterviewSessionResponse,
  sourceContext: CandidateContext
): void {
  if (!findSession(sourceContext))
    throw new Error('An interview transition requires its initiating session');
  acceptCandidateSession(response, {
    kind: 'session',
    session_id: response.session_id,
    transportId: findSession(sourceContext)?.transportId,
  });
}

/** Called only after the launch secret has been removed from browser history. */
export function completeCandidateLaunch(launchCredential: string): void {
  sessionStore.removePersisted(`${LAUNCH_NONCE_PREFIX}${launchCredential}`);
}

export function peekCapability(context: CandidateContext): string | undefined {
  const session = findSession(context)?.session;
  if (!session) return undefined;
  return freshCapability(session);
}
function freshCapability(
  session: InterviewSessionResponse
): string | undefined {
  const expiry = Date.parse(session.expires_at);
  return Number.isFinite(expiry) && expiry > Date.now() + 30_000
    ? session.capability_token
    : undefined;
}

/**
 * Asks an invitation exchange to also return the first reads of the page the
 * link opens (POST /candidate_capability `entry`), which `onReads` receives
 * once the session is stored. Only the caller that starts the exchange gets
 * them.
 */
export interface CandidateEntryRequest {
  /** The UI language the settings' translated policies are read in. */
  uiLang: string;
  /**
   * The page whose reads to return. Left out for the landing page, the
   * server's default, so that request is the same for a server deployed before
   * the field.
   */
  page?: InterviewEntryPage;
  onReads: (reads: InterviewEntryReads) => void;
}

const entryFields = ({
  uiLang,
  page,
}: CandidateEntryRequest): InterviewEntryRequest => ({
  ui_lang: uiLang,
  ...(page ? { page } : {}),
});

export async function ensureCandidateSession(
  context: CandidateContext,
  entry?: CandidateEntryRequest
): Promise<InterviewSessionResponse> {
  context = bindCandidateRequestTransport(context) ?? context;
  const stored =
    context.kind === 'session'
      ? readSession(context.session_id, true)
      : findSession(context);
  if (stored && freshCapability(stored.session)) {
    if (context.kind === 'launch') requirePersistedAuthority(stored);
    return stored.session;
  }
  const key = stored
    ? `session:${stored.session.session_id}`
    : contextKey(context);
  const pending = inFlight.get(key);
  const request =
    pending ??
    exchange(context, stored, entry).finally(() => {
      if (inFlight.get(key) === request) inFlight.delete(key);
    });
  if (!pending) inFlight.set(key, request);
  const session = await request;
  if (context.kind !== 'launch') return session;
  // Refresh work is shared by SID, but discarding a launch URL is a separate
  // caller decision. A normal request must not grant another caller that proof.
  const acknowledged = findSession(context);
  if (!acknowledged || acknowledged.session.session_id !== session.session_id)
    throw new Error('Interview session response does not match its request');
  requirePersistedAuthority(acknowledged);
  return acknowledged.session;
}
async function exchange(
  context: CandidateContext,
  stored: StoredSession | undefined,
  entry: CandidateEntryRequest | undefined
): Promise<InterviewSessionResponse> {
  if (!stored && context.kind === 'launch') {
    const key = `${LAUNCH_NONCE_PREFIX}${context.launch_credential}`;
    const persistedNonce = readPersisted(key);
    if (persistedNonce && persistedNonce !== context.redemption_nonce) {
      throw new Error('Interview launch nonce does not match this tab');
    }
    // A consumed launch can be retried only with this exact nonce. Verify it
    // survives reload before spending the one-time authority, and retain it if
    // the subsequent session write fails.
    requirePersistence(key, context.redemption_nonce);
  }
  // Lazy import breaks the generated-client / axios-interceptor dependency cycle.
  const { getCall } = await import('api/generated/api/call');
  const api = getCall();
  const requestContext = stored?.transportId
    ? { ...context, transportId: stored.transportId }
    : context;
  const transportOptions = requestContext.transportId
    ? { candidateContext: requestContext }
    : {};
  let response: InterviewSessionResponse;
  let entryReads: InterviewEntryReads | null = null;
  if (stored) {
    response = await api.callRefreshInterviewSession(
      {
        session_id: stored.session.session_id,
        refresh_token: stored.session.refresh_token,
      },
      { timeout: EXCHANGE_TIMEOUT_MS, ...transportOptions }
    );
  } else if (context.kind === 'web_call_link') {
    const mint = (entryRequest: CandidateEntryRequest | undefined) =>
      api.callCreateCandidateCapability(
        {
          kind: context.kind,
          encoded_id: context.encoded_id,
          ...(entryRequest ? { entry: entryFields(entryRequest) } : {}),
        },
        {
          timeout: EXCHANGE_TIMEOUT_MS,
          ...transportOptions,
        }
      );
    // An external agent's transport exchanges through its own route, which
    // has no reads.
    const entryRequest = requestContext.transportId ? undefined : entry;
    let minted: Awaited<ReturnType<typeof mint>>;
    try {
      minted = await mint(entryRequest);
    } catch (error) {
      // A server deployed before the entry reads rejects the field (422) but
      // still exchanges the invitation without it.
      if (
        !entryRequest ||
        !isAxiosError(error) ||
        error.response?.status !== 422
      )
        throw error;
      minted = await mint(undefined);
    }
    // The reads are page data, never part of the stored session.
    const { entry: reads, ...issued } = minted;
    response = issued;
    entryReads = reads ?? null;
  } else if (context.kind === 'launch') {
    response = await api.callExchangeInterviewLaunch(
      {
        launch_credential: context.launch_credential,
        redemption_nonce: context.redemption_nonce,
      },
      { timeout: EXCHANGE_TIMEOUT_MS, ...transportOptions }
    );
  } else {
    throw new Error(
      'This interview session is unavailable in this tab. Open your interview link again.'
    );
  }
  if (stored && response.session_id !== stored.session.session_id)
    throw new Error('Interview session response does not match its request');
  acceptCandidateSession(
    response,
    stored
      ? {
          kind: 'session',
          session_id: stored.session.session_id,
          transportId: requestContext.transportId,
        }
      : requestContext
  );
  if (entryReads) entry?.onReads(entryReads);
  return response;
}
export async function ensureCapability(
  context: CandidateContext | null = captureCandidateContext()
): Promise<string | null> {
  if (!context) return null;
  return (await ensureCandidateSession(context)).capability_token;
}

/** A rotated call token may update only the session captured before its request. */
export function setCapabilityFromResponse(
  token: string | null | undefined,
  context?: CandidateContext | null
): void {
  if (!token) return;
  if (!context)
    throw new Error(
      'A candidate response requires its captured request context'
    );
  const stored = findSession(context);
  const claims = tokenClaims(token);
  if (!stored || claims?.sid !== stored.session.session_id)
    throw new Error('Candidate response token belongs to another session');
  const expiry = capabilityTokenExpiryMs(token);
  if (!expiry) throw new Error('Candidate response token has no expiry');
  persist({
    ...stored,
    session: {
      ...stored.session,
      capability_token: token,
      expires_at: new Date(expiry).toISOString(),
    },
  });
}

/** Retain refresh authority while expiring only the exact token rejected by the server. */
export function invalidateCapabilityToken(
  token: string,
  context: CandidateContext
): void {
  const stored = findSession(context);
  if (stored?.session.capability_token !== token) return;
  updateSession({
    ...stored,
    session: { ...stored.session, expires_at: new Date(0).toISOString() },
  });
}

export function buildSessionLaunchUrl(
  path: string,
  launchCredential: string,
  sourceContext: CandidateContext | null = null
): string {
  const url = new URL(path, window.location.origin);
  url.hash = new URLSearchParams({ launch: launchCredential }).toString();
  return mapCandidateLaunchUrl(url.toString(), sourceContext);
}

export function resetCandidateCapabilityStateForTests({
  keepPersisted = false,
}: { keepPersisted?: boolean } = {}): void {
  sessions.clear();
  inFlight.clear();
  launchNonces.clear();
  resolvedLaunches.clear();
  currentRouteParams = null;
  if (!keepPersisted) {
    for (const key of sessionStore.keys()) {
      if (
        key.startsWith(SESSION_PREFIX) ||
        key.startsWith(LAUNCH_NONCE_PREFIX) ||
        key.startsWith('candidateCapability:')
      )
        sessionStore.removePersisted(key);
    }
  }
}
